import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { ActivitySpinnerProps, ActivitySpinnerSize, ActivitySpinnerWidget } from '../ActivitySpinnerWidget';
import { caseInsensitiveValueMatch, isFocusableItem, isSelectableItem, ListItem } from "../List/ListItemUtils";

export interface TreeItem extends Omit<ListItem, 'disabled' | 'group' | 'value'> {
  /** A disabled item is shown with dimmed visual styling. Disabled items are focusable and expandable but not selectable. */
  disabled?: boolean;
  /**
   * When true, this item will be rendered as a section title for sibling items until the next group or separator sibling item is rendered.
   *
   * This does not create an additional element with `role=group` because that role is reserved for indicating parent-child relationship per ARIA spec.
   */
  group?: boolean;
  /**
   * This is used to determine the overall hierarchy.
   * For example, if an item has `value: 'abc'` and another item has `parentValue: 'abc'`, the latter will be nested under the former.
   *
   * Must be truthy unless this item is at top level. Must match the `value` of another `TreeItem` in the same tree.
   */
  parentValue?: string;
  /**
   * This is used as the unique key for tracking which items are selected, expanded, and/or focused.
   *
   * Must be truthy unless `separator` is `true`. Must be unique among all `TreeItem` in the same tree.
   */
  value?: string;
}

export interface TreeNode {
  /** the nodes nested under this node */
  children: Array<TreeNode>;
  /** the item this node was created from */
  item: TreeItem;
}

export interface TreeHierarchyInfo {
  /** a set of all values associated with parent nodes */
  allParentValues: Set<string>;
  /** a set of all values associated with parent nodes that have loading nodes underneath */
  allParentValuesLoadingWithin: Set<string>;
  /** a set of all values associated with leaf nodes that are selectable */
  allSelectableLeafValues: Set<string>;
  /** a set of all values associated with nodes that are selectable */
  allSelectableValues: Set<string>;
  /** a map for looking up any node by the associated `value` */
  nodeMapByValue: Map<string, TreeNode>;
  /** an array of the top level nodes (created from `TreeItem`s that have falsy `parentValue`) */
  topLevelNodes: Array<TreeNode>;
}

export const constructTreeNodeHierarchy = (items: Array<TreeItem> | null, unselectableParents: boolean, multiselect?: boolean): TreeHierarchyInfo => {
  const allParentValues = new Set<string>();
  const allParentValuesLoadingWithin = new Set<string>();
  const allSelectableLeafValues = new Set<string>();
  const allSelectableValues = new Set<string>();
  const nodeMapByValue = new Map<string, TreeNode>();
  const topLevelNodes: Array<TreeNode> = [];

  items?.forEach((item, index) => {
    // separators have no `value` so generate a key in order to place them in the overall hierarchy of nodes
    nodeMapByValue.set(!item.value ? `separator%%${index}` : item.value, { children: [], item: item });
    if (isSelectableTreeItem(item)) {
      allSelectableLeafValues.add(item.value!);
      allSelectableValues.add(item.value!);
    }
  });

  nodeMapByValue.forEach(node => {
    if (!node.item.parentValue) {
      topLevelNodes.push(node);
    } else {
      const parentNode = nodeMapByValue.get(node.item.parentValue);
      if (parentNode) {
        parentNode.children.push(node);
        allParentValues.add(node.item.parentValue);
        allSelectableLeafValues.delete(node.item.parentValue);
        unselectableParents && allSelectableValues.delete(node.item.parentValue);
        if (isContentLoading(node.item) && multiselect) {
          getAncestorValues(nodeMapByValue, node.item.value!).forEach(ancestorValue => {
            allParentValuesLoadingWithin.add(ancestorValue);
            allSelectableValues.delete(ancestorValue);
          });
        }
      } else {
        throw new Error(`Missing item with value='${node.item.parentValue}'. Required because another item has parentValue='${node.item.parentValue}'.`);
      }
    }
  });

  return { allParentValues, allParentValuesLoadingWithin, allSelectableLeafValues, allSelectableValues, nodeMapByValue, topLevelNodes };
};

/**
 * @returns whether a tree item is selectable, taking into account whether it has content loading
 */
const isSelectableTreeItem = (item: TreeItem): boolean => {
  return isSelectableItem(item) && !isContentLoading(item);
};

export type TreeItemFilterFn<TreeItemType extends TreeItem = TreeItem> = (item: TreeItemType, searchText: string) => boolean;

/**
 * @returns
 * `matchedValues` - a set of values associated with nodes that match the passed in `searchText` determined using `filterFn`
 *
 * `matchedValuesAncestors` - a set of values associated with ancestors of matched nodes
 *
 * `topLevelNodesFiltered` - a copy of the passed in `topLevelNodes` with unmatched branches taken out and with special treatment for group or separator nodes
 */
export const filterTree = (topLevelNodes: TreeHierarchyInfo['topLevelNodes'], searchText: string, filterFn: TreeItemFilterFn = caseInsensitiveValueMatch)
  : { matchedValues: Set<string>, matchedValuesAncestors: Set<string>, topLevelNodesFiltered: TreeHierarchyInfo['topLevelNodes'] } => {
  const matchedValues = new Set<string>();
  const matchedValuesAncestors = new Set<string>();

  if (!searchText) {
    return { matchedValues, matchedValuesAncestors, topLevelNodesFiltered: topLevelNodes };
  }

  const topLevelNodesFiltered = new Array<TreeNode>();
  const currentNodeByLevel = new Array<TreeNode>();
  const groupOrSeparatorNodeByLevel = new Array<TreeNode | undefined>();

  traverseTree(
    topLevelNodes,
    (node: TreeNode, level: number) => { // this block happens before traversing descendants
      currentNodeByLevel[level] = { item: node.item, children: [] };
      if (node.item.group || node.item.separator) {
        groupOrSeparatorNodeByLevel[level] = node;
      } else if (filterFn(node.item, searchText)) {
        node.item.value && matchedValues.add(node.item.value);
        for (let i = 0; i < level; i++) {
          const ancestorValue = currentNodeByLevel[i].item.value;
          ancestorValue && matchedValuesAncestors.add(ancestorValue);
        }
      }
    },
    () => true,
    0,
    (node: TreeNode, level: number) => { // this block happens affter traversing descendants
      if (!node.item.value) { return; }

      const placeToInsert = level > 0 ? currentNodeByLevel[level - 1].children : topLevelNodesFiltered;
      const isMatch = matchedValues.has(node.item.value);
      if (isMatch) {
        currentNodeByLevel[level].children = node.children;
      }
      if (isMatch || matchedValuesAncestors.has(node.item.value)) {
        const groupOrSeparatorNode = groupOrSeparatorNodeByLevel[level];
        if (groupOrSeparatorNode) {
          groupOrSeparatorNodeByLevel[level] = undefined;
          if (placeToInsert.length || !groupOrSeparatorNode.item.separator) {
            placeToInsert.push(groupOrSeparatorNode);
          }
        }
        placeToInsert.push(currentNodeByLevel[level]);
      }
    },
  );

  return { matchedValues, matchedValuesAncestors, topLevelNodesFiltered };
};

/**
 * @returns
 * `focusableValues` - an array of values associated with focusable nodes, excluding any nodes that would not be shown because their parents are not expanded
 *
 * `treeHeight` - the number of levels on the longest path from a top level node to a leaf node
 */
export const getFocusableValuesAndTreeHeight = (topLevelNodes: TreeHierarchyInfo['topLevelNodes'], expandedValues: Set<string>)
  : { focusableValues: Array<string>, treeHeight: number } => {
  const focusableValues: Array<string> = [];
  let treeHeight = 0;

  traverseTree(
    topLevelNodes,
    (node: TreeNode, level?: number) => {
      isFocusableItem(node.item) && focusableValues.push(node.item.value!);
      if (typeof level === 'number') {
        treeHeight = Math.max(treeHeight, level);
      }
    },
    (node: TreeNode) => expandedValues.has(node.item.value!),
    0,
  );

  return { focusableValues, treeHeight };
};

/**
 * @returns an array of values associated with ancestor nodes of the node with the given value
 */
export const getAncestorValues = (nodeMapByValue: TreeHierarchyInfo['nodeMapByValue'], value: string): Array<string> => {
  const ancestorValues: Array<string> = [];
  let currentNode = nodeMapByValue.get(value);
  while (currentNode && currentNode.item.parentValue) {
    ancestorValues.push(currentNode.item.parentValue);
    currentNode = nodeMapByValue.get(currentNode.item.parentValue);
  }

  return ancestorValues;
};

/**
 * @returns an array of values associated with descendant leaf nodes of the node with the given value
 */
export const getDescendantLeafValues = (nodeMapByValue: TreeHierarchyInfo['nodeMapByValue'], value: string): Array<string> => {
  const descendantLeafValues: Array<string> = [];
  const currentNode = nodeMapByValue.get(value);
  if (currentNode && currentNode.children.length) {
    traverseTree(
      currentNode.children,
      (node: TreeNode) => {
        if (node.item.value && !node.item.group && !node.item.separator && !node.children.length) {
          descendantLeafValues.push(node.item.value);
        }
      },
      () => true,
    );
  }

  return descendantLeafValues;
};

const traverseTree = (
  nodes: Array<TreeNode>,
  performAction: (node: TreeNode, level?: number) => void,
  shouldIncludeChildren: (node: TreeNode) => boolean,
  level?: number,
  performActionAfter?: (node: TreeNode, level?: number) => void,
): void => {
  nodes.forEach(node => {
    performAction(node, level);
    if (shouldIncludeChildren(node)) {
      traverseTree(node.children, performAction, shouldIncludeChildren, typeof level === 'number' ? level + 1 : level, performActionAfter);
    }
    performActionAfter?.(node, level);
  });
};

/** A special component that can be passed as `TreeItem.content` to indicate the item is loading while meeting accessibility standards */
export const LoadingItemContent: React.FC<Omit<ActivitySpinnerProps, 'spinnerSize'>> = (props) => {
  return <ActivitySpinnerWidget {...props} alt={Messages.loading()} spinnerSize={ActivitySpinnerSize.Small} />;
};

LoadingItemContent.displayName = 'LoadingItemContent';

export const isContentLoading = (item: TreeItem): boolean => React.isValidElement(item.content) && item.content.type === LoadingItemContent;
